import { createSelector } from 'reselect';

import { LandlordType, UserType } from 'ha/constants/UserType';
import { getDay, getMonth, getYear } from '@ha/date';
import { GlobalState } from 'ha/types/store';

const getAuth = (s: GlobalState) => s.authLogic;

export const getUser = (s: GlobalState) => s.authLogic.user;

const getUserInformation = (s: GlobalState) => s.authLogic.userInformation;

export const getUserFullName = (s: GlobalState) => {
  const user = getUser(s);

  if (!user) {
    return null;
  }

  return `${user.firstName} ${user.lastName}`;
};

export const getIsLoading = (s: GlobalState) => getAuth(s).isLoading;
export const getIsLoaded = (s: GlobalState) => getAuth(s).isLoaded;

export const getCurrentLanguage = (s: GlobalState) => getAuth(s).language;

export const getIsAuthenticated = (s: GlobalState) =>
  getAuth(s).isAuthenticated;

export const getUserPhone = (s: GlobalState) =>
  s.authLogic.user ? s.authLogic.user.phone : null;

export const getUserFirstName = (s: GlobalState) =>
  s.authLogic.user ? s.authLogic.user.firstName : null;

export const getUserLastName = (s: GlobalState) =>
  s.authLogic.user ? s.authLogic.user.lastName : null;
export const getUserType = (s: GlobalState) =>
  s.authLogic.user ? s.authLogic.user.type : null;
export const getUserEmail = (s: GlobalState) =>
  s.authLogic.user ? s.authLogic.user.email : null;
export const getCountryCode = (s: GlobalState) =>
  s.authLogic.user ? s.authLogic.user.countryCode : null;
export const getUserId = (s: GlobalState) =>
  s.authLogic.user ? s.authLogic.user.id : null;
export const getUserUUID = (s: GlobalState) =>
  s.authLogic.user ? s.authLogic.user.uuid : '';
export const getIsAdmin = (s: GlobalState) =>
  s.authLogic.user ? s.authLogic.user.isAdmin : false;
export const getIsPartner = (s: GlobalState) =>
  s.authLogic.user ? s.authLogic.user.isPartner : null;
export const getUserUniversity = (s: GlobalState) =>
  s.authLogic.user ? s.authLogic.user.study : null;
export const getUserBirthDate = (s: GlobalState) =>
  s.authLogic.user ? s.authLogic.user.birthDate : null;
export const getUserEmailVerified = (s: GlobalState) =>
  s.authLogic.user ? s.authLogic.user.isEmailVerified : null;
export const getUserEnableSMSNotifications = (s: GlobalState) =>
  s.authLogic.user ? s.authLogic.user.enableSMSNotifications : null;
export const getUserNotificationLang = (s: GlobalState) =>
  s.authLogic.user ? s.authLogic.user.notificationLang : null;
export const getUserBirthDay = (s: GlobalState) => {
  const date = getUserBirthDate(s);

  if (!date) {
    return date;
  }

  return getDay(date);
};
export const getUserBirthMonth = (s: GlobalState) => {
  const date = getUserBirthDate(s);

  if (!date) {
    return date;
  }

  return getMonth(date) + 1;
};
export const getUserBirthYear = (s: GlobalState) => {
  const date = getUserBirthDate(s);

  if (!date) {
    return date;
  }

  return getYear(date);
};
export const getUserSpokenLanguages = (s: GlobalState) =>
  s.authLogic.user ? s.authLogic.user.spokenLanguages : [];
export const getIsAliased = (s: GlobalState) =>
  s.authLogic.user ? s.authLogic.user.isAliased : false;

export const getUserNationality = (s: GlobalState) => {
  const { user } = s.authLogic;

  if (!user) {
    return user;
  }

  return /^[A-Z]{2}$/.test(user.nationality || '') ? user.nationality : '';
};

export const getUserAvatar = (s: GlobalState) =>
  s.authLogic.user && s.authLogic.user.avatarURL;

export const getResponseRate = createSelector([getUser], user =>
  user ? Math.round(user.responseRate * 100) : 0,
);

export const isResponseRateDisplaying = createSelector(
  [getUser, getUserInformation],
  (user, userInformation) =>
    !!(userInformation?.listingsCount && user?.responseRate),
);

export const getIsVerified = createSelector(
  [getIsAuthenticated, getUserType, getIsAdmin],
  (isAuthenticated, userType, isAdmin) =>
    isAuthenticated &&
    ([
      UserType.LANDLORD,
      LandlordType.STUDENT,
      LandlordType.PRIVATE_LANDLORD,
      LandlordType.RENTAL_COMPANY,
      LandlordType.OTHER,
    ].includes(userType!) ||
      isAdmin),
);

export const getIsAdvertiser = createSelector(
  [getIsAuthenticated, getUserType, getIsAdmin],
  (isAuthenticated, userType, isAdmin) =>
    isAuthenticated &&
    ([UserType.STUDENT, UserType.LANDLORD, UserType.PENDING].includes(
      userType!,
    ) ||
      isAdmin),
);

export const getIsUnderReview = createSelector(
  [getUserType],
  userType => userType === UserType.UNDER_REVIEW,
);

export const getUserIdAlias = createSelector(
  [getUserId],
  userId => `u${userId}`,
);

export const getIsUserInformationLoading = (s: GlobalState) =>
  s.authLogic.isUserBillingInfoLoading;

export const hasCompletedBookings = (s: GlobalState) =>
  s.authLogic.userInformation &&
  s.authLogic.userInformation.advertiserBookingsCount > 0;

export const isMissingBillingDetails = (s: GlobalState) =>
  s.authLogic.userInformation &&
  // eslint-disable-next-line no-prototype-builtins
  s.authLogic.userInformation.hasOwnProperty('hasCompleteBillingInformation') &&
  !s.authLogic.userInformation.hasCompleteBillingInformation;

export const isMissingPayoutMethod = (s: GlobalState) =>
  s.authLogic.userInformation &&
  s.authLogic.userInformation.savedPayoutMethodsCount === 0;

export const getHasOtherListings = (s: GlobalState) =>
  s.authLogic.userInformation && s.authLogic.userInformation.listingsCount > 1;

export const getHasListings = (s: GlobalState) =>
  (s.authLogic.userInformation &&
    s.authLogic.userInformation.listingsCount > 0) ||
  false;

export const isAuthenticatedWithBookings = (s: GlobalState) =>
  getIsAuthenticated(s) && hasCompletedBookings(s);

export const getImpersonatingAdmin = (s: GlobalState) =>
  s.authLogic.impersonatingAdmin;

export const getUserTraits = createSelector(
  [getUser, getIsVerified, getIsAdmin, getImpersonatingAdmin],
  (user, isVerified, isAdmin, isImpersonated) => {
    if (!user) {
      return null;
    }

    return {
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      usertype: user.type,
      landlordType:
        user.landlordType === LandlordType.OTHER
          ? user.landlordTypeOther
          : user.landlordType,
      phone: user.phone,
      createdAt: user.createdAt,
      verified: isVerified,
      isAdmin,
      isImpersonated: Boolean(isImpersonated),
    };
  },
);
